import {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {ActionIcon, Badge, Menu, TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Card} from 'src/design/atoms/Card';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useMediaQuery} from '@mantine/hooks';
import {IconDots, IconSearch} from '@tabler/icons-react';
import {
  CourseClassroomAttendanceEnum,
  CourseClassroomAttendeeResultEnum,
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import type {CourseClassroomAttendeeRosterRowResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {OptionButtonGroup} from 'src/design/atoms/OptionButtonGroup';
import {Spinner} from 'src/design/atoms/Spinner';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {
  useAddCourseClassroomAttendeeBatchMutation,
  useGetCourseClassroomQuery,
  useGetCourseClassroomRosterQuery,
  useUpdateCourseClassroomAttendeeMutation,
} from 'src/redux/queries/courseClassroom';
import {useGetCourseDefinitionQuery} from 'src/redux/queries/courseDefinition';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {TrainingDocumentTitle} from 'src/app/training/courseContentValidation';
import {RosterBuilderModal} from '../ClassWorkspace/components/RosterBuilderModal';

const attendanceLabel: Record<CourseClassroomAttendanceEnum, string> = {
  [CourseClassroomAttendanceEnum.Invalid]: 'Unmarked',
  [CourseClassroomAttendanceEnum.Registered]: 'Unmarked',
  [CourseClassroomAttendanceEnum.Present]: 'Present',
  [CourseClassroomAttendanceEnum.Absent]: 'Absent',
  [CourseClassroomAttendanceEnum.Excused]: 'Excused',
};

const resultLabel: Record<CourseClassroomAttendeeResultEnum, string> = {
  [CourseClassroomAttendeeResultEnum.Invalid]: 'Pending',
  [CourseClassroomAttendeeResultEnum.Pending]: 'Pending',
  [CourseClassroomAttendeeResultEnum.Passed]: 'Passed',
  [CourseClassroomAttendeeResultEnum.Failed]: 'Failed',
  [CourseClassroomAttendeeResultEnum.Waived]: 'Waived',
};

const resultBadgeColor: Record<CourseClassroomAttendeeResultEnum, string> = {
  [CourseClassroomAttendeeResultEnum.Invalid]: 'gray',
  [CourseClassroomAttendeeResultEnum.Pending]: 'gray',
  [CourseClassroomAttendeeResultEnum.Passed]: 'green',
  [CourseClassroomAttendeeResultEnum.Failed]: 'red',
  [CourseClassroomAttendeeResultEnum.Waived]: 'gray',
};

const ATTENDANCE_OPTIONS = [
  {value: 'present', label: 'Present', color: 'green' as const},
  {value: 'absent', label: 'Absent', color: 'red' as const},
];

const RESULT_OPTIONS = [
  {value: 'passed', label: 'Pass', color: 'green' as const},
  {value: 'failed', label: 'Fail', color: 'red' as const},
];

/**
 * Day-of mode — tablet-first attendance marking for one classroom. Online
 * only (no outbox; the offline core this originally depended on is not on
 * develop). Cards, not a data grid, and the row/card tap is the control
 * (sanctioned convention break). Zero page navigations while marking.
 */
export const ClassroomRunPage = () => {
  useSetDocumentTitle(TrainingDocumentTitle);
  const {definitionId, classroomId} = useParams();
  const {setAlert} = useAlert();
  // Four side-by-side mark buttons only fit from lg up; narrower viewports get
  // the stacked segmented groups the mobile evaluation flow calls for.
  const isWide = useMediaQuery('(min-width: 62em)');

  const [search, setSearch] = useState('');
  const [walkInOpened, setWalkInOpened] = useState(false);
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  const [localOverrides, setLocalOverrides] = useState<
    Record<number, CourseClassroomAttendanceEnum>
  >({});
  const [pendingResultIds, setPendingResultIds] = useState<Set<number>>(
    new Set()
  );
  const [localResultOverrides, setLocalResultOverrides] = useState<
    Record<number, CourseClassroomAttendeeResultEnum>
  >({});

  const {data: definition} = useGetCourseDefinitionQuery(
    parseInt(definitionId!),
    {
      skip: !definitionId,
    }
  );
  const {data: classroom} = useGetCourseClassroomQuery(parseInt(classroomId!), {
    skip: !classroomId,
  });
  const {
    data: rosterRows,
    isLoading,
    error,
  } = useGetCourseClassroomRosterQuery(parseInt(classroomId!), {
    skip: !classroomId,
  });

  const [updateAttendee] = useUpdateCourseClassroomAttendeeMutation();
  const [addAttendeeBatch, {isLoading: isAddingWalkIns}] =
    useAddCourseClassroomAttendeeBatchMutation();

  useEffect(() => {
    if (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [error]);

  const roster = useMemo(
    () =>
      (rosterRows ?? []).map(row => ({
        ...row,
        attendance: localOverrides[row.id] ?? row.attendance,
        result: localResultOverrides[row.id] ?? row.result,
      })),
    [rosterRows, localOverrides, localResultOverrides]
  );

  const filteredRoster = useMemo(
    () =>
      search.trim() === ''
        ? roster
        : roster.filter(row => {
            const term = search.trim().toLowerCase();
            return (
              row.userName.toLowerCase().includes(term) ||
              (row.employeeId?.toLowerCase().includes(term) ?? false)
            );
          }),
    [roster, search]
  );

  const counts = useMemo(() => {
    let present = 0;
    let absent = 0;
    let unmarked = 0;
    roster.forEach(row => {
      if (row.attendance === CourseClassroomAttendanceEnum.Present) present++;
      else if (
        row.attendance === CourseClassroomAttendanceEnum.Absent ||
        row.attendance === CourseClassroomAttendanceEnum.Excused
      )
        absent++;
      else unmarked++;
    });
    return {present, absent, unmarked};
  }, [roster]);

  const handleMark = async (
    row: CourseClassroomAttendeeRosterRowResponse,
    attendance: CourseClassroomAttendanceEnum
  ) => {
    const previous = localOverrides[row.id] ?? row.attendance;
    // Optimistic UI: no outbox, this wave is online-only (Wave 4 decision).
    setLocalOverrides(current => ({...current, [row.id]: attendance}));
    setPendingIds(current => new Set(current).add(row.id));

    try {
      await updateAttendee({id: row.id, attendance}).unwrap();
    } catch (submitError) {
      setLocalOverrides(current => ({...current, [row.id]: previous}));
      setAlert(
        `Could not mark ${row.userName} as ${
          attendanceLabel[attendance]
        }: ${parseErrorMessage(submitError)}`,
        'error'
      );
    }

    setPendingIds(current => {
      const next = new Set(current);
      next.delete(row.id);
      return next;
    });
  };

  const handleMarkResult = async (
    row: CourseClassroomAttendeeRosterRowResponse,
    result: CourseClassroomAttendeeResultEnum
  ) => {
    const previous = localResultOverrides[row.id] ?? row.result;
    setLocalResultOverrides(current => ({...current, [row.id]: result}));
    setPendingResultIds(current => new Set(current).add(row.id));

    try {
      await updateAttendee({id: row.id, result}).unwrap();
    } catch (submitError) {
      setLocalResultOverrides(current => ({...current, [row.id]: previous}));
      setAlert(
        `Could not mark ${row.userName} as ${
          resultLabel[result]
        }: ${parseErrorMessage(submitError)}`,
        'error'
      );
    }

    setPendingResultIds(current => {
      const next = new Set(current);
      next.delete(row.id);
      return next;
    });
  };

  const handleAddWalkIns = async (assignmentIds: number[]) => {
    if (classroomId === undefined) {
      return;
    }
    try {
      await addAttendeeBatch({
        classroomId: parseInt(classroomId),
        assignmentIds,
      }).unwrap();
      setAlert(
        `Added ${assignmentIds.length} walk-in${
          assignmentIds.length === 1 ? '' : 's'
        }.`,
        'success'
      );
      setWalkInOpened(false);
    } catch (addError) {
      setAlert(parseErrorMessage(addError), 'error');
    }
  };

  const {breadcrumbs, fallbackTo: exitPath} = useTrainingBreadcrumbs({
    mode: 'classroom-run',
    definitionId,
    courseName: definition?.name,
    classroomId,
    classroomIdentifier: classroom?.internalIdentifier,
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <Box
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'var(--mantine-color-body)',
          paddingBottom: 'var(--mantine-spacing-sm)',
        }}>
        <BackBar
          fallbackTo={exitPath}
          fallbackLabel="Exit to Workspace"
          breadcrumbs={breadcrumbs}
        />
        <Group justify="space-between" mt="xs" mb="xs" wrap="wrap">
          <Text fw={700} size="lg">
            {definition?.name} {classroom?.internalIdentifier}
          </Text>
          <Button onClick={() => setWalkInOpened(true)}>Add Walk-in</Button>
        </Group>
        <Group justify="space-between" wrap="wrap" mb="xs">
          <Group gap="xs">
            <Badge color="green" size="lg">
              Present {counts.present}
            </Badge>
            <Badge color="red" size="lg">
              Absent {counts.absent}
            </Badge>
            <Badge color="gray" size="lg">
              Unmarked {counts.unmarked}
            </Badge>
          </Group>
        </Group>
        <TextInput
          placeholder="Search name or employee ID"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
          size="md"
        />
      </Box>

      {isLoading ? (
        <Spinner loadingText="Loading roster...." />
      ) : filteredRoster.length === 0 ? (
        <Alert color="gray" mt="md">
          {roster.length === 0
            ? 'No one is on the roster yet. Use "Add Walk-in" to enroll someone.'
            : 'No attendees match your search.'}
        </Alert>
      ) : (
        <Stack gap="sm" mt="md">
          {filteredRoster.map(row => {
            const identity = (
              <Box style={{minWidth: 0, flex: 1}}>
                <Group gap="xs" wrap="nowrap">
                  <Text fw={600} size="lg" truncate>
                    {row.userName}
                  </Text>
                  {row.result ===
                    CourseClassroomAttendeeResultEnum.Waived && (
                    <Badge color={resultBadgeColor[row.result]} size="sm">
                      {resultLabel[row.result]}
                    </Badge>
                  )}
                </Group>
                <Text size="sm" c="dimmed" truncate>
                  {[row.employeeId, row.departmentName, row.vendorName]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </Text>
              </Box>
            );

            const markButtons = (
              <Group gap="xs" wrap="nowrap">
                <Button
                  color="green"
                  variant={
                    row.attendance === CourseClassroomAttendanceEnum.Present
                      ? 'filled'
                      : 'outline'
                  }
                  size="lg"
                  miw={100}
                  mih={44}
                  loading={pendingIds.has(row.id)}
                  onClick={() =>
                    handleMark(row, CourseClassroomAttendanceEnum.Present)
                  }>
                  Present
                </Button>
                <Button
                  color="red"
                  variant={
                    row.attendance === CourseClassroomAttendanceEnum.Absent
                      ? 'filled'
                      : 'outline'
                  }
                  size="lg"
                  miw={100}
                  mih={44}
                  loading={pendingIds.has(row.id)}
                  onClick={() =>
                    handleMark(row, CourseClassroomAttendanceEnum.Absent)
                  }>
                  Absent
                </Button>
                <Divider orientation="vertical" />
                <Button
                  color="green"
                  variant={
                    row.result === CourseClassroomAttendeeResultEnum.Passed
                      ? 'filled'
                      : 'outline'
                  }
                  size="lg"
                  miw={100}
                  mih={44}
                  loading={pendingResultIds.has(row.id)}
                  onClick={() =>
                    handleMarkResult(
                      row,
                      CourseClassroomAttendeeResultEnum.Passed
                    )
                  }>
                  Pass
                </Button>
                <Button
                  color="red"
                  variant={
                    row.result === CourseClassroomAttendeeResultEnum.Failed
                      ? 'filled'
                      : 'outline'
                  }
                  size="lg"
                  miw={100}
                  mih={44}
                  loading={pendingResultIds.has(row.id)}
                  onClick={() =>
                    handleMarkResult(
                      row,
                      CourseClassroomAttendeeResultEnum.Failed
                    )
                  }>
                  Fail
                </Button>
              </Group>
            );

            const overflowMenu = (
              <Menu position="bottom-end">
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size={44}
                    aria-label={`More options for ${row.userName}`}>
                    <IconDots size={20} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    onClick={() =>
                      handleMark(row, CourseClassroomAttendanceEnum.Excused)
                    }>
                    Mark Excused
                  </Menu.Item>
                  <Menu.Item
                    onClick={() =>
                      handleMark(
                        row,
                        CourseClassroomAttendanceEnum.Registered
                      )
                    }>
                    Reset to Unmarked
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    disabled={pendingResultIds.has(row.id)}
                    onClick={() =>
                      handleMarkResult(
                        row,
                        CourseClassroomAttendeeResultEnum.Waived
                      )
                    }>
                    Mark Waived
                  </Menu.Item>
                  <Menu.Item
                    disabled={pendingResultIds.has(row.id)}
                    onClick={() =>
                      handleMarkResult(
                        row,
                        CourseClassroomAttendeeResultEnum.Pending
                      )
                    }>
                    Reset Result to Pending
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            );

            const segmentedMarks = (
              <Stack gap="xs">
                <OptionButtonGroup
                  size="lg"
                  ariaLabel={`Attendance for ${row.userName}`}
                  options={ATTENDANCE_OPTIONS}
                  value={
                    row.attendance === CourseClassroomAttendanceEnum.Present
                      ? 'present'
                      : row.attendance === CourseClassroomAttendanceEnum.Absent
                      ? 'absent'
                      : null
                  }
                  allowDeselect={false}
                  disabled={pendingIds.has(row.id)}
                  onChange={value => {
                    if (!value) return;
                    void handleMark(
                      row,
                      value === 'present'
                        ? CourseClassroomAttendanceEnum.Present
                        : CourseClassroomAttendanceEnum.Absent
                    );
                  }}
                />
                <OptionButtonGroup
                  size="lg"
                  ariaLabel={`Result for ${row.userName}`}
                  options={RESULT_OPTIONS}
                  value={
                    row.result === CourseClassroomAttendeeResultEnum.Passed
                      ? 'passed'
                      : row.result === CourseClassroomAttendeeResultEnum.Failed
                      ? 'failed'
                      : null
                  }
                  allowDeselect={false}
                  disabled={pendingResultIds.has(row.id)}
                  onChange={value => {
                    if (!value) return;
                    void handleMarkResult(
                      row,
                      value === 'passed'
                        ? CourseClassroomAttendeeResultEnum.Passed
                        : CourseClassroomAttendeeResultEnum.Failed
                    );
                  }}
                />
              </Stack>
            );

            return (
              <Card key={row.id} withBorder shadow="xs" p="md">
                {isWide ? (
                  <Group justify="space-between" wrap="nowrap">
                    {identity}
                    <Group gap="xs" wrap="nowrap">
                      {markButtons}
                      {overflowMenu}
                    </Group>
                  </Group>
                ) : (
                  <Stack gap="sm">
                    <Group justify="space-between" wrap="nowrap">
                      {identity}
                      {overflowMenu}
                    </Group>
                    {segmentedMarks}
                  </Stack>
                )}
              </Card>
            );
          })}
        </Stack>
      )}

      {classroom && (
        <RosterBuilderModal
          opened={walkInOpened}
          onClose={() => setWalkInOpened(false)}
          courseDefinitionId={classroom.courseDefinitionId}
          capacity={classroom.capacity}
          currentRosterCount={roster.length}
          existingAssignmentIds={roster.map(row => row.assignmentId)}
          isSaving={isAddingWalkIns}
          onAdd={handleAddWalkIns}
        />
      )}
    </ContentWrapper>
  );
};

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.TrainingCourseClassroomRun(':definitionId', ':classroomId'),
  element: <ClassroomRunPage />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.Update,
  isPrivate: true,
});
